import { Injectable, computed, effect, inject, signal } from '@angular/core';

import {
  BillingPlanKey,
  FALLBACK_FORGE_PRICING_PLANS,
  PricingPlanDefinition,
  PricingPlanQuantities
} from './pricing-cart.models';
import { RuntimeConfigService } from '../../core/config/runtime-config.service';

type PersistedPricingCartState = {
  companyName: string;
  checkoutEmail: string;
  quantities: PricingPlanQuantities;
};

export type PricingCartLineItem = {
  key: BillingPlanKey;
  name: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  priceLabel: string;
};

const STORAGE_KEY = 'gf_pricing_cart_v1';

function createEmptyQuantities(): PricingPlanQuantities {
  return {};
}

function normalizeQuantity(value: unknown): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return Math.max(0, Math.floor(numericValue));
}

function normalizeEmail(value: unknown): string {
  return String(value || '').trim();
}

function normalizeCompanyName(value: unknown): string {
  return String(value || '').trim();
}

function hasBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function normalizePlans(plans: PricingPlanDefinition[]): PricingPlanDefinition[] {
  return plans.map((plan) => ({
    ...plan,
    key: String(plan.key || '').trim(),
    unitPrice: Number(plan.unitPrice || 0),
    features: Array.isArray(plan.features) ? plan.features : [],
  })).filter((plan) => plan.key);
}

function readPersistedState(): PersistedPricingCartState {
  if (!hasBrowserStorage()) {
    return {
      companyName: '',
      checkoutEmail: '',
      quantities: createEmptyQuantities()
    };
  }

  const fallbackState: PersistedPricingCartState = {
    companyName: '',
    checkoutEmail: '',
    quantities: createEmptyQuantities()
  };

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return fallbackState;
    }

    const parsed = JSON.parse(raw) as Partial<PersistedPricingCartState>;
    const persistedQuantities = parsed.quantities ?? createEmptyQuantities();

    return {
      companyName: normalizeCompanyName(parsed.companyName),
      checkoutEmail: normalizeEmail(parsed.checkoutEmail),
      quantities: Object.fromEntries(
        Object.entries(persistedQuantities || {}).map(([planKey, quantity]) => [planKey, normalizeQuantity(quantity)])
      )
    };
  } catch {
    return fallbackState;
  }
}

@Injectable({ providedIn: 'root' })
export class PricingCartService {
  private readonly runtimeConfig = inject(RuntimeConfigService);

  private readonly persistedState = readPersistedState();
  private readonly quantitiesState = signal<PricingPlanQuantities>(this.persistedState.quantities);
  private readonly companyNameState = signal(this.persistedState.companyName);
  private readonly checkoutEmailState = signal(this.persistedState.checkoutEmail);
  private readonly drawerOpenState = signal(false);

  readonly quantities = computed(() => this.quantitiesState());
  readonly companyName = computed(() => this.companyNameState());
  readonly checkoutEmail = computed(() => this.checkoutEmailState());
  readonly drawerOpen = computed(() => this.drawerOpenState());
  readonly plans = computed<PricingPlanDefinition[]>(() => {
    const runtimePlans = this.runtimeConfig.tryGetConfig()?.pricing?.publicPlans ?? [];
    if (runtimePlans.length > 0) {
      return normalizePlans(
        runtimePlans.map((plan) => ({
          key: plan.key,
          name: plan.name,
          unitPrice: Number(plan.unitPriceEuros || 0),
          priceLabel: plan.priceLabel,
          badge: plan.badge,
          description: plan.description,
          features: plan.features,
          highlight: plan.highlight,
          salesOnly: plan.salesOnly,
        }))
      );
    }

    return normalizePlans(FALLBACK_FORGE_PRICING_PLANS);
  });
  readonly selfServePlans = computed(() =>
    this.plans().filter((plan) => !plan.salesOnly)
  );
  readonly salesAssistedPlans = computed(() =>
    this.plans().filter((plan) => plan.salesOnly)
  );
  readonly hasSelfServePlans = computed(() => this.selfServePlans().length > 0);
  readonly hasSalesAssistedPlans = computed(() => this.salesAssistedPlans().length > 0);
  readonly lines = computed<PricingCartLineItem[]>(() => {
    const quantities = this.quantitiesState();
    return this.plans()
      .map((plan) => {
        const quantity = Number(quantities[plan.key] || 0);
        if (quantity <= 0 || plan.salesOnly) {
          return null;
        }

        return {
          key: plan.key,
          name: plan.name,
          unitPrice: plan.unitPrice,
          quantity,
          lineTotal: plan.unitPrice * quantity,
          priceLabel: plan.priceLabel
        };
      })
      .filter((line): line is PricingCartLineItem => line !== null);
  });
  readonly totalSeats = computed(() =>
    this.lines().reduce((sum, line) => sum + line.quantity, 0)
  );
  readonly totalMonthly = computed(() =>
    this.lines().reduce((sum, line) => sum + line.lineTotal, 0)
  );
  readonly hasItems = computed(() => this.totalSeats() > 0);

  constructor() {
    effect(() => {
      if (!hasBrowserStorage()) {
        return;
      }

      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          companyName: this.companyNameState(),
          checkoutEmail: this.checkoutEmailState(),
          quantities: this.quantitiesState()
        } satisfies PersistedPricingCartState)
      );
    });
  }

  getQuantity(planKey: BillingPlanKey): number {
    return Number(this.quantitiesState()[planKey] || 0);
  }

  getPlanTotal(planKey: BillingPlanKey): number {
    const plan = this.plans().find((candidate) => candidate.key === planKey);
    if (!plan) {
      return 0;
    }
    return plan.unitPrice * this.getQuantity(planKey);
  }

  setQuantity(planKey: BillingPlanKey, quantity: unknown): void {
    const normalizedQuantity = normalizeQuantity(quantity);
    this.quantitiesState.update((current) => ({
      ...current,
      [planKey]: normalizedQuantity
    }));
  }

  increment(planKey: BillingPlanKey): void {
    this.setQuantity(planKey, this.getQuantity(planKey) + 1);
  }

  decrement(planKey: BillingPlanKey): void {
    this.setQuantity(planKey, this.getQuantity(planKey) - 1);
  }

  ensurePlan(planKey: BillingPlanKey, minimumQuantity = 1): void {
    const plan = this.plans().find((candidate) => candidate.key === planKey);
    if (plan?.salesOnly) {
      return;
    }

    const normalizedMinimum = Math.max(1, normalizeQuantity(minimumQuantity));
    if (this.getQuantity(planKey) >= normalizedMinimum) {
      return;
    }

    this.setQuantity(planKey, normalizedMinimum);
  }

  remove(planKey: BillingPlanKey): void {
    this.setQuantity(planKey, 0);
  }

  setCheckoutEmail(email: string): void {
    this.checkoutEmailState.set(normalizeEmail(email));
  }

  setCompanyName(companyName: string): void {
    this.companyNameState.set(normalizeCompanyName(companyName));
  }

  replaceQuantities(quantities: Partial<PricingPlanQuantities>): void {
    this.quantitiesState.update((current) => {
      const nextState = { ...current };
      Object.entries(quantities || {}).forEach(([planKey, quantity]) => {
        nextState[planKey] = normalizeQuantity(quantity);
      });
      return nextState;
    });
  }

  openDrawer(): void {
    this.drawerOpenState.set(true);
  }

  closeDrawer(): void {
    this.drawerOpenState.set(false);
  }

  toggleDrawer(): void {
    this.drawerOpenState.update((open) => !open);
  }

  formatAmount(amount: number): string {
    const safeAmount = Number.isFinite(amount) ? Math.max(0, Math.round(amount)) : 0;
    return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(safeAmount)}€`;
  }
}
